import { useEffect, useState } from 'react';
import Button from "../../atoms/Button";
import InfoBox from "../../atoms/InfoBox";
import styles from "./UserPreferences.module.scss";
import Modal from "../Modal";
import ModalCurrentTherapist from "../ModalCurrentTherapist";
import FormHeader from "../../atoms/FormHeader";
import {trpc} from "../../../utils/trpc";
import Input from "../../atoms/Input";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import routes from "routes";
import {useAppDispatch} from "customHooks/reduxHooks";
import {setInfo} from "store/slices/infoSlice";
import {analyticsService} from "../../../utils/analyticsService";
import TherapistFeedbackModal from "../TherapistFeedbackModal";
import { raiseException } from "../../../utils/raiseException";
import { useTherapistData } from '../../../customHooks/therapistDataContext';
import InformedConsentModal from '../../Modals/InformedConsentModal';
import { checkInformedConsentStatus } from '../../../utils/informedConsentStatus'

export const UserPreferences = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const {state} = location;

	const dispatch = useAppDispatch();
	const [userGender, setUserGender] = useState("");
	const [therapist, setTherapist] = useState<any>(null);
	const [showFeedbackModal, setShowFeedbackModal] = useState(false);
	const {therapistData} = useTherapistData();

	useEffect(() => {
		analyticsService({
			'event': 'virtualPageview',
			'pageUrl': '/dashboard/profilo/preferenze',
			'pageTitle': 'Patient Preferences'
		});
	}, []);

	const searchParams = useSearchParams()[0];
	let informedConsentParam = searchParams.get('consenso-informato');

	const [informedConsentModalIsOpen, setInformedConsentModalIsOpen] =
		useState(false);
	const [signatureModal, setSignatureModal] = useState(false);
	const [showCurrentTherapistModal, setShowCurrentTherapistModal] =
		useState(false);
	const [changePreferencesModalIsOpen, setChangePreferencesModalIsOpen] =
		useState(false);
	const [informedConsetIsCompiled, setInformedConsentIsCompiled] =
		useState(false);

	const [changeMotive, setChangeMotive] = useState("");
	const [userTherapy, setUserTherapy] = useState<
		"single" | "couple" | "underage" | "psychiatry"
	>("single");

	const getMyTherapist = trpc.mainService.patient.getMyTherapist.useQuery(
		{therapistId: therapistData?.therapistId || undefined}
	).data?.therapist;


	const userInfo = trpc.mainService.user.getCurrentUserInfo.useQuery({therapistId: therapistData?.therapistId || undefined});

	const {data: informedConsentResponse, isLoading: isInformedConsentLoading} =
		trpc.mainService.patient.getinformedConsent.useQuery({
			accountTherapyType: therapistData?.therapyType || undefined,
			additionalTherapistId: therapistData?.therapistId || undefined
		});

	const sendChangeTherapistFeedback =
		trpc.mainService.patient.sendChangeTherapistFeedback.useMutation();

	const sendInformedConsent =
		trpc.mainService.patient.sendInformedConsent.useMutation();

	const onSendChangeTherapistFeedback = async (elementToChange: "therapist" | "preferences") => {
		try {
			await sendChangeTherapistFeedback.mutateAsync({feedback: changeMotive})
			navigate(routes.Survey.links.start, {
				state: {
					user: userInfo?.data?.user,
					sendNewSurvey: "sendNewSurvey",
				},
			});
		} catch (error: any) {
			raiseException(error);
			console.error("ERROR", error?.shape?.message)
		}
	};

	useEffect(() => {
		if (getMyTherapist) {
			setTherapist(getMyTherapist);
			if (state?.informedConsentIsOpen) {
				setInformedConsentModalIsOpen(true);
			} else if (informedConsentParam && informedConsentParam === 'open') {
				setInformedConsentModalIsOpen(true);
			}
		}
	}, [getMyTherapist]);

	useEffect(() => {
		if (userInfo.data) {
			setUserGender(userInfo.data.user.gender!);
			setUserTherapy(therapistData?.therapyType);
		}
	}, [userInfo.data]);

	useEffect(() => {
		checkInformedConsentStatus(informedConsentResponse, setInformedConsentIsCompiled);
	}, [informedConsentResponse]);

	return (
		<div className={styles.userPreferences}>
			{informedConsentModalIsOpen && !isInformedConsentLoading && (
				<InformedConsentModal
					userTherapy={userTherapy}
					therapist={therapist}
					therapistData={therapistData}
					setInformedConsentModalIsOpen={setInformedConsentModalIsOpen}
					userInfo={userInfo?.data?.user}
					informedConsentResponse={informedConsentResponse}
					signatureModal={signatureModal}
					informedConsetIsCompiled={informedConsetIsCompiled}
					setSignatureModal={setSignatureModal}
					page="preferences"
				/>
			)}
			{(showFeedbackModal && userInfo?.isSuccess) &&
				<TherapistFeedbackModal
					myTherapist={getMyTherapist}
					userInfo={userInfo?.data?.user}
					modalClose={() => setShowFeedbackModal(false)}
				/>
			}
			{(showCurrentTherapistModal && therapist) && (
				<ModalCurrentTherapist
					therapist={therapist}
					modalClose={() => setShowCurrentTherapistModal(false)}
					goToFeedbackModal={() => setShowFeedbackModal(true)}
				/>
			)}
			{changePreferencesModalIsOpen && (
				<Modal close={() => {
					setChangePreferencesModalIsOpen(false);
				}}>
					<div className={styles.changePreferences} onClick={e => {
						e.stopPropagation()
					}}>
						<FormHeader
							closeModal={() => setChangePreferencesModalIsOpen(false)}
							title={`Sei sicur${
								userGender === "male"
									? "o"
									: userGender === "female"
										? "a"
										: "*"
							} di voler cambiare le tue preferenze?`}
							closeButton
						/>
						<div className={styles.descritpion}>
							Ti consigliamo di parlarne prima con il tuo terapeuta per capire
							se effettivamente il tuo percorso non soddisfa le tue aspettative
							e quindi provare a vedere se un altra scelta possa fare al caso
							tuo. Motiva la scelta qui sotto:
						</div>
						<Input
							textarea
							value={changeMotive}
							onChange={(e) => setChangeMotive(e.target.value)}
						/>
						<div className={styles.btnWrapper}>
							<Button
								variant="secondary"
								onClick={() => setChangePreferencesModalIsOpen(false)}
							>
								No, ci penso su
							</Button>
							<Button
								disabled={changeMotive.trim() === ""}
								onClick={() => onSendChangeTherapistFeedback("preferences")}
							>
								Sì, mostrami altri terapeuti
							</Button>
						</div>
					</div>
				</Modal>
			)}
			<h1 className={styles.title}>Gestione preferenze</h1>
			<div className={styles.preferencesContainer}>
				<div className={styles.prefernce}>
					<div className={styles.preferenceTitle}>Consenso informato</div>
					{informedConsetIsCompiled ? (
						<div className={styles.preferenceDescription}>
							Hai compilato correttamente il Consenso Informato.{" "}
							{isInformedConsentLoading || userInfo.isLoading ? (
								""
							) : (userTherapy === "underage") &&
							!informedConsentResponse?.otherInformedConsentParent ? (
								<>
                  <span>
                    Per poter partecipare alle sedute il consenso deve essere
                    firmato anche dall'altro genitore, tramite il link ricevuto
                    sulla sua casella postale. Clicca il bottone sottostante per
                    inviare di nuovo il link.
                  </span>
									{!userInfo?.data?.user?.parentEmail && (
										<>
											<br/>
											<span style={{marginTop: 20}}>
                        Il pulsante "invia consenso informato al genitore" non
                        risulta cliccabile perchè l'email del genitore non è
                        stata correttamente inserita. Vai nella sezione{" "}
												<span
													onClick={() => {
														navigate(routes.ProfileSection.links.info);
													}}
													style={{
														cursor: "pointer",
														textDecoration: "underline",
													}}
												>
                          informazioni personali
                        </span>{" "}
												e modificala.
                      </span>
										</>
									)}
								</>
							) : (
								<>
									Se in futuro vorrai modificare il consenso potrai farlo
									contattando il Supporto. Puoi visualizzare l’Informativa
									Privacy al{" "}
									<span
										className={styles.underlinedText}
										onClick={() => {
											window.open(`${userTherapy !== "psychiatry" ? 'https://mymentis.it/privacy-paziente-terapeuta/' : 'https://mymentis.it/privacy-policy-paziente-psc-ci/' }`)
										}}
									>
                    seguente link.
                  </span>
									{(userInfo?.data && userTherapy === "couple") &&
										!userInfo.data.user.partnerEmail && (
											<>
												<br/>
												<span style={{marginTop: 20}}>
                          Il pulsante "invia consenso informato al tuo partner"
                          non risulta cliccabile perchè l'email del partner non
                          è stata correttamente inserita. Vai nella sezione{" "}
													<span
														onClick={() => {
															navigate(routes.ProfileSection.links.info);
														}}
														style={{
															cursor: "pointer",
															textDecoration: "underline",
														}}
													>
                            informazioni personali
                          </span>{" "}
													e modificala.
                        </span>
											</>
										)}
								</>
							)}
						</div>
					) : (
						<div className={styles.preferenceDescription}>
							Per iniziare le {userTherapy !== "psychiatry" ? 'sedute' : 'visite'} è necessario fornire il consenso tramite la
							compilazione del form che apparirà cliccando sul pulsante in
							basso. Se in futuro vorrai modificare il consenso potrai farlo
							contattando il Supporto. Puoi visualizzare l’Informativa Privacy
							al{" "}
							<span
								className={styles.underlinedText}
								onClick={() => {
									window.open(`${userTherapy !== "psychiatry" ? 'https://mymentis.it/privacy-paziente-terapeuta/' : 'https://mymentis.it/privacy-policy-paziente-psc-ci/' }`)
								}}
							>
                seguente link
              </span>
							.
						</div>
					)}
				</div>
				{!informedConsetIsCompiled && (
					<InfoBox
						className={styles.consensInfo}
						text="Il consenso informato ha lo scopo di informare la persona assistita riguardo le modalità e le finalità delle prestazioni che riceverà, promuovendo l'autonomia dell'individuo sulle scelte riguardanti il proprio benessere psico-fisico (art. 24 del codice deontologico)."
					/>
				)}
				<div className={styles.btnWrapper}>
					{informedConsetIsCompiled ? (
						<Button
							onClick={() => {
								setSignatureModal(true);
								setInformedConsentModalIsOpen(true)
							}}
							disabled={
								(userTherapy === "underage" &&
									!userInfo.data?.user?.soleGuardianship &&
									!informedConsentResponse?.otherInformedConsentParent) ||
								(userTherapy === "couple" &&
									!informedConsentResponse?.otherInformedConsent)
							}
						>
							Visualizza il consenso informato
						</Button>
					) : (
						<Button
							onClick={() => {
								setInformedConsentModalIsOpen(true);
							}}
						>
							Compila il consenso informato
						</Button>
					)}
					{((informedConsentResponse?.otherInformedConsent === null &&
							userTherapy === 'couple') ||
						(informedConsentResponse?.otherInformedConsentParent === null && !userInfo.data?.user?.soleGuardianship &&
							userTherapy === 'underage')) && (
						<Button
							onClick={async () => {
								if (!userInfo.data?.user.otherParentEmail) {
									return navigate(routes.ProfileSection.links.info);
								}
								await sendInformedConsent.mutateAsync({
									therapistId: therapistData?.therapistId,
									accountTherapyType: therapistData?.therapyType
								}).catch((e) => {
									raiseException(e);
									dispatch(
										setInfo({
											variant: "error",
											text: "Prova a ricaricare la pagina e verifica di essere connsso a internet",
											title: "Si è verificato un errore",
										})
									);
								});
								dispatch(
									setInfo({
										variant: "confirm",
										text: "Email inviata con successo",
										title:
											userTherapy === 'couple'
												? "Il consenso informato è stato inviato con successo al tuo partner"
												: "Il consenso informato è stato inviato con successo al genitore",
									})
								);
							}}
							disabled={
								(userTherapy === 'couple' &&
									!userInfo.data?.user?.partnerEmail) ||
								(userTherapy === 'underage' &&
									!userInfo.data?.user?.parentEmail)
							}
						>
							{userTherapy === 'couple'
								? "Invia consenso informato al tuo partner"
								: "Invia consenso informato al genitore"}
						</Button>
					)}
				</div>
			</div>
			<div className={styles.preferencesContainer}>
				<div className={styles.prefernce}>
					<div className={styles.preferenceTitle}>
						Il tuo {userTherapy !== "psychiatry" ? 'terapeuta' : 'psichiatra'} non ti convince?
					</div>
					<div className={styles.preferenceDescription}>
						Ripeti il questionario per modificare le tue preferenze e poter
						scegliere un nuovo professionista oppure scegli direttamente un
						nuovo {userTherapy !== 'psychiatry' ? 'terapeuta' : 'psichiatra'} senza cambiare le
						preferenze del questionario.
					</div>
				</div>
				<div className={styles.btnWrapper}>
					{(therapistData?.main ? !userInfo.data?.user.changeTherapist : !therapistData?.changeTherapist) && (
						<Button
							onClick={() => {
								setShowCurrentTherapistModal(true);
							}}
						>
							Scegli un nuovo {userTherapy !== "psychiatry" ? 'terapeuta' : 'psichiatra'}
						</Button>
					)}
				</div>
				{(therapistData?.main ? userInfo.data?.user.changeTherapist : therapistData?.changeTherapist) && (
					<div className={styles.prefernce}>
						<div className={styles.preferenceDescription}>
							Hai già eseguito un cambio {userTherapy !== "psychiatry" ? 'terapeuta' : 'psichiatra'}, al momento non è possibile farne altri.
							Scrivi a <a href="mailto: clientsupport@mymentis.it">clientsupport@mymentis.it</a> se hai bisogno di
							assistenza.
						</div>
					</div>
				)}
			</div>
		</div>
	);
};
